import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// zcallSfu — couche de préparation du SERVEUR MÉDIA (SFU).
//
// Objectif : ouvrir les LIVES et les grandes visioconférences. Tant que le SFU
// n'est pas configuré (SuperAdmin → Paramètres → Serveur média), ZCall reste en
// WebRTC « maillage » (peer-to-peer). Dès qu'un SFU est activé, la salle bascule
// automatiquement sur ce transport évolutif, sans changer l'expérience.
//
// Les identifiants (clé/secret) ne descendent JAMAIS jusqu'au navigateur : le
// client demande un jeton éphémère à la fonction serveur `zcall-token`.
// ─────────────────────────────────────────────────────────────

export type SfuProvider = 'livekit';

export interface SfuConfig {
  enabled: boolean;
  provider: SfuProvider;
  url: string;
}

export interface SfuSession {
  token: string;
  url: string;
  provider: SfuProvider;
}

const DEFAULT_CONFIG: SfuConfig = { enabled: false, provider: 'livekit', url: '' };

/** Lit la configuration publique du serveur média (best-effort). */
export async function fetchSfuConfig(): Promise<SfuConfig> {
  try {
    const { data } = await supabase
      .from('zifek')
      .select('sfu_enabled, sfu_provider, sfu_url')
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (!data) return DEFAULT_CONFIG;
    const url = String(data.sfu_url || '').trim();
    return {
      enabled: !!data.sfu_enabled && url.length > 0,
      provider: 'livekit',
      url,
    };
  } catch {
    return DEFAULT_CONFIG;
  }
}

/** Demande un jeton d'accès au serveur média pour une salle donnée. */
export async function requestSfuToken(
  room: string,
  identity: string,
  name: string,
): Promise<SfuSession | null> {
  try {
    const { data, error } = await supabase.functions.invoke('zcall-token', {
      body: { room, identity, name },
    });
    if (error) return null;
    const res = data as { token?: string; url?: string; provider?: string } | null;
    if (!res?.token || !res?.url) return null;
    return { token: res.token, url: res.url, provider: 'livekit' };
  } catch {
    return null;
  }
}